import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, FALSE_ALARMS, REAL_BUG, WRONG_FIX, REAL_FIX, VERIFIED, LESSONS, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 92 }) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

const mono = (extra: React.CSSProperties = {}): React.CSSProperties => ({
  fontFamily: FONTS.mono,
  fontSize: 24,
  lineHeight: 1.7,
  color: COLORS.text,
  ...extra,
});

/* 01 — HOOK: audited one chat, most "bugs" were false alarms */
const FLAGS = [
  { label: "grok model", real: false },
  { label: "grounding error", real: false },
  { label: "in_progress step", real: false },
  { label: "leaked markers", real: true },
];
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="build-in-public · one real audit" color={COLORS.cyan} />
          <div style={{ display: "flex", gap: 14, marginBottom: 34 }}>
            {FLAGS.map((f, i) => {
              const on = interpolate(frame, [18 + i * 12, 40 + i * 12], [0.12, 1], {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              });
              const c = f.real ? COLORS.red : COLORS.green;
              return (
                <div
                  key={f.label}
                  style={{
                    opacity: on,
                    fontFamily: FONTS.mono,
                    fontSize: 20,
                    color: c,
                    border: `1px solid ${c}66`,
                    background: COLORS.panel,
                    borderRadius: 8,
                    padding: "8px 16px",
                    boxShadow: `0 0 26px -14px ${c}`,
                  }}
                >
                  {f.real ? "✗" : "✓"} {f.label}
                </div>
              );
            })}
          </div>
          <Big size={88}>
            I flagged four bugs.
            <br />
            Most were <span style={{ color: COLORS.green }}>false alarms</span>.
          </Big>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 24,
              color: COLORS.dim,
              marginTop: 26,
            }}
          >
            one was <span style={{ color: COLORS.red }}>real</span> — and hiding a layer upstream
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — THE FALSE ALARMS: three flags that were actually fine */
const ALARMS = [
  { flag: "named model: grok", verdict: "accurate & grounded", detail: "that part of the answer checked out against the sources" },
  { flag: "grounding-check error", verdict: "working as intended", detail: "the check correctly flagged a claim the sources didn't support" },
  { flag: "step stuck in_progress", verdict: "just half a pair", detail: "the first half of a normal two-step pair — nothing broke" },
];
export const Scene02FalseAlarms: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.falseAlarms.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 60px" }}>
        <Kicker text="what looked broken — but wasn't" color={COLORS.green} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          {ALARMS.map((a, i) => {
            const app = interpolate(frame, [20 + i * 26, 46 + i * 26], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={a.flag}
                style={{
                  opacity: app,
                  transform: `translateY(${(1 - app) * 20}px)`,
                }}
              >
                <Panel accent="green" title={`flag ${i + 1}`} width={540}>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.amber, marginBottom: 12 }}>
                    ⚑ {a.flag}
                  </div>
                  <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 30, color: COLORS.green, marginBottom: 14 }}>
                    ✓ {a.verdict}
                  </div>
                  <div style={{ fontFamily: FONTS.body, fontSize: 21, lineHeight: 1.5, color: COLORS.dim }}>
                    {a.detail}
                  </div>
                </Panel>
              </div>
            );
          })}
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 22,
            color: COLORS.dim,
            marginTop: 34,
            opacity: interpolate(frame, [330, 360], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
          }}
        >
          three of four flags: <span style={{ color: COLORS.green }}>false alarms</span>
        </div>
      </AbsoluteFill>
      <Caption lines={FALSE_ALARMS} accent="green" />
    </AbsoluteFill>
  );
};

/* 03 — THE ONE REAL BUG: leaked internal chunk-map markers */
export const Scene03RealBug: React.FC = () => {
  const frame = useCurrentFrame();
  const stamp = interpolate(frame, [250, 280], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.realBug.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the one real bug · architecture answer" color={COLORS.red} />
          <Panel accent="red" title="generated answer · leaked internals" width={1200}>
            <div style={mono({ fontSize: 25, lineHeight: 1.8 })}>
              EAKC is a multi-tenant RAG service{" "}
              <span style={{ color: COLORS.red, background: `${COLORS.red}1A`, padding: "1px 6px", borderRadius: 5 }}>
                [File Index: src/lib/rag/retrieval.ts|1-40|ts]
              </span>{" "}
              with a sync worker{" "}
              <span style={{ color: COLORS.red, background: `${COLORS.red}1A`, padding: "1px 6px", borderRadius: 5 }}>
                [File Inde… src/worker|1-
              </span>{" "}
              per the{" "}
              <span style={{ color: COLORS.red, background: `${COLORS.red}1A`, padding: "1px 6px", borderRadius: 5 }}>
                [Knowledge Graph]
              </span>
              .
              <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim, marginTop: 20, lineHeight: 1.7 }}>
                ↳ raw file-index markers, <span style={{ color: COLORS.red }}>copied into prose</span>
                <br />
                ↳ often <span style={{ color: COLORS.red }}>malformed / half-truncated</span>
                <br />
                ↳ a <span style={{ color: COLORS.red }}>[Knowledge Graph]</span> citation that never existed
              </div>
            </div>
          </Panel>
          <div
            style={{
              marginTop: 26,
              opacity: stamp,
              transform: `scale(${0.9 + stamp * 0.1})`,
              fontFamily: FONTS.mono,
              fontSize: 26,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: COLORS.red,
              border: `2px solid ${COLORS.red}`,
              padding: "8px 22px",
              borderRadius: 8,
            }}
          >
            internal plumbing · leaked to the user
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={REAL_BUG} accent="red" />
    </AbsoluteFill>
  );
};

/* 04 — THE WRONG FIX: patched the render, not the injection */
const PATH: PipeNode[] = [
  { label: "codebase map", value: "raw headers", sub: "the real source", accent: "red" },
  { label: "prompt build", value: "injected", sub: "model now sees them", accent: "amber" },
  { label: "model", value: "copies them", sub: "into the answer", accent: "amber" },
  { label: "output render", value: "fix #1 here", sub: "✗ one layer too late", accent: "magenta" },
];
export const Scene04WrongFix: React.FC = () => {
  const frame = useCurrentFrame();
  const note = interpolate(frame, [230, 262], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.wrongFix.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
        <Kicker text="8-expert review · fix the source, not the symptom" color={COLORS.magenta} />
        <div style={{ width: 1760 }}>
          <PipelineGraph nodes={PATH} startFrame={10} stepFrames={28} nodeWidth={280} />
        </div>
        <div style={{ marginTop: 46, opacity: note, maxWidth: 1160 }}>
          <Callout label="↳ where the leak actually starts" accent="red" width={1160}>
            fix #1 scrubbed the <b style={{ color: COLORS.text }}>rendered output</b> — but the marker was already in the answer.
            The leak begins upstream, where the <b style={{ color: COLORS.text }}>codebase map injects raw file-index headers into the prompt</b>.
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={WRONG_FIX} accent="magenta" />
    </AbsoluteFill>
  );
};

/* 05 — THE REAL FIX: rewrite headers before the model sees them */
export const Scene05RealFix: React.FC = () => {
  const frame = useCurrentFrame();
  const after = interpolate(frame, [150, 185], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.realFix.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the real fix · at the injection point" color={COLORS.green} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <Panel accent="red" title="what the model used to see" width={640}>
            <div style={mono({ fontSize: 22, color: COLORS.red })}>
              [File Index: src/lib/rag/
              <br />
              retrieval.ts|1-40|ts]
            </div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 19, color: COLORS.dim, marginTop: 14 }}>
              raw marker · easy to copy into prose
            </div>
          </Panel>
          <div style={{ alignSelf: "center", fontSize: 40, color: COLORS.green }}>→</div>
          <div style={{ opacity: after, alignSelf: "stretch" }}>
            <Panel accent="green" title="what the model sees now" width={640}>
              <div style={mono({ fontSize: 22, color: COLORS.green })}>
                File: src/lib/rag/retrieval.ts
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 19, color: COLORS.dim, marginTop: 14 }}>
                clean line · nothing internal to leak
              </div>
            </Panel>
          </div>
        </div>
        <div style={{ marginTop: 30, opacity: after, maxWidth: 1120 }}>
          <Callout label="↳ why this holds everywhere" accent="green" width={1120}>
            rewritten <b style={{ color: COLORS.text }}>before the prompt is built</b> — the model can&rsquo;t leak what it never sees, so the
            <b style={{ color: COLORS.text }}> live stream is clean too</b>, not just the saved answer.
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={REAL_FIX} accent="green" />
    </AbsoluteFill>
  );
};

/* 06 — VERIFIED ON PROD: 4/9/25 → 0/0/0 (the only proof numbers) */
const COUNTS = [
  { was: 4, label: "leaked markers", accent: "cyan" as const },
  { was: 9, label: "fake citations", accent: "magenta" as const },
  { was: 25, label: "broken fragments", accent: "amber" as const },
];
export const Scene06Verified: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.verified.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="verified on prod · pulled both chats from the DB" color={COLORS.green} />
        <div style={{ display: "flex", gap: 90, alignItems: "flex-start" }}>
          {COUNTS.map((c, i) => (
            <div key={c.label} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
                <span
                  style={{
                    fontFamily: FONTS.display,
                    fontWeight: 700,
                    fontSize: 56,
                    color: COLORS.dim,
                    textDecoration: "line-through",
                  }}
                >
                  {c.was}
                </span>
                <span style={{ fontFamily: FONTS.mono, fontSize: 34, color: COLORS.dim }}>→</span>
                <MetricCounter value={0} from={c.was} startFrame={20 + i * 16} durationFrames={45} accent="green" fontSize={128} />
              </div>
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 22,
                  color: COLORS.dim,
                  marginTop: 6,
                  textAlign: "center",
                }}
              >
                {c.label}
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            color: COLORS.dim,
            marginTop: 44,
            opacity: interpolate(frame, [150, 185], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
          }}
        >
          same architecture question · re-run on production · <span style={{ color: COLORS.green }}>0 / 0 / 0</span>
        </div>
      </AbsoluteFill>
      <Caption lines={VERIFIED} accent="green" />
    </AbsoluteFill>
  );
};

/* 07 — THE LESSONS */
export const Scene07Lessons: React.FC = () => {
  const frame = useCurrentFrame();
  const two = interpolate(frame, [150, 185], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.lessons.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="what the audit was really worth" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <Panel accent="cyan" title="lesson 1" width={620}>
            <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 34, color: COLORS.text, marginBottom: 14 }}>
              Verify before you fix.
            </div>
            <div style={{ fontFamily: FONTS.body, fontSize: 22, lineHeight: 1.5, color: COLORS.dim }}>
              most trace flags were false alarms — spend the fix budget on the one that&rsquo;s actually real.
            </div>
          </Panel>
          <div style={{ opacity: two, alignSelf: "stretch" }}>
            <Panel accent="green" title="lesson 2" width={620}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 34, color: COLORS.text, marginBottom: 14 }}>
                Trace the real data path.
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 22, lineHeight: 1.5, color: COLORS.dim }}>
                not the plausible one — the leak began a full layer upstream of the render.
              </div>
            </Panel>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={LESSONS} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={82}>
          Most <span style={{ color: COLORS.amber }}>&ldquo;bugs&rdquo;</span> aren&rsquo;t.
          <br />
          The real one hid <span style={{ color: COLORS.cyan }}>one layer upstream</span>.
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · build-in-public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
